// ProjectionModal — Phase 7. Opens GET /api/deals/{id}/projection and renders
// a 5- or 10-year cash-flow projection with sale-exit + IRR. Lets the user
// toggle the holding horizon and the local/home currency view.

import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, TrendingUp, Calendar, ArrowLeftRight, AlertCircle } from "lucide-react";

function fmtCcy(value, currency) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(Number(value));
  } catch {
    return `${currency} ${Math.round(Number(value)).toLocaleString()}`;
  }
}

function fmtPct(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  return `${(Number(value) * 100).toFixed(1)}%`;
}

export default function ProjectionModal({ open, onOpenChange, dealId }) {
  const [years, setYears] = useState(5);
  const [view, setView] = useState("home"); // "local" | "home"
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !dealId) return;
    let alive = true;
    setLoading(true);
    setError(null);
    api
      .get(`/deals/${dealId}/projection`, { params: { holding_years: years } })
      .then(({ data }) => {
        if (alive) setData(data);
      })
      .catch((e) => {
        if (alive) setError(e?.response?.data?.detail || "Could not load projection.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [open, dealId, years]);

  const series = data ? (view === "local" ? data.local : data.home) : null;
  const ccy = data ? (view === "local" ? data.local_currency : data.home_currency) : "USD";
  const sameCcy = data && data.local_currency === data.home_currency;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="projection-modal"
        className="max-w-3xl border-slate-200 bg-white"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <TrendingUp className="h-4 w-4 text-indigo-600" />
            Projection — {years}-year hold
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            Forward cash-flow + sale-exit + IRR using country-specific capital gains.
            Spot FX, no hedging assumption.
          </DialogDescription>
        </DialogHeader>

        {/* Controls */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
            {[5, 10].map((y) => (
              <button
                key={y}
                type="button"
                data-testid={`proj-years-${y}`}
                onClick={() => setYears(y)}
                className={`inline-flex items-center gap-1 rounded-md px-3 py-1 text-xs font-semibold transition ${
                  years === y ? "bg-white text-indigo-700 shadow-sm" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                <Calendar className="h-3 w-3" /> {y} yr
              </button>
            ))}
          </div>

          {!sameCcy && (
            <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
              <button
                type="button"
                data-testid="proj-view-local"
                onClick={() => setView("local")}
                className={`rounded-md px-3 py-1 text-xs font-semibold transition ${
                  view === "local" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"
                }`}
              >
                Local · {data?.local_currency}
              </button>
              <button
                type="button"
                data-testid="proj-view-home"
                onClick={() => setView("home")}
                className={`rounded-md px-3 py-1 text-xs font-semibold transition ${
                  view === "home" ? "bg-white text-indigo-700 shadow-sm" : "text-slate-500 hover:text-slate-900"
                }`}
              >
                <ArrowLeftRight className="mr-1 inline h-3 w-3" />
                Home · {data?.home_currency}
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex h-48 items-center justify-center text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <AlertCircle className="mr-1.5 inline h-4 w-4" /> {error}
          </div>
        ) : series && data ? (
          <div className="space-y-5">
            {/* IRR + cumulative + verdict */}
            <div className="grid gap-3 sm:grid-cols-3">
              <div
                data-testid="proj-irr-card"
                className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4 shadow-sm"
              >
                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-indigo-700">
                  Project IRR
                </div>
                <div className="mt-1 text-3xl font-bold tracking-tight text-indigo-900 tabular-nums">
                  {data.irr === null || data.irr === undefined ? "N/A" : fmtPct(data.irr)}
                </div>
                <p className="mt-0.5 text-[11px] text-indigo-700/80">
                  Internal Rate of Return, all-in, currency-neutral.
                </p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Cum. cash flow ({years}yr)
                </div>
                <div className="mt-1 text-2xl font-bold tabular-nums text-slate-900">
                  {fmtCcy(series.cumulative_after_tax_cash_flow, ccy)}
                </div>
                <p className="mt-0.5 text-[11px] text-slate-500">After tax, before sale.</p>
              </div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4 shadow-sm">
                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-700">
                  Net sale proceeds
                </div>
                <div className="mt-1 text-2xl font-bold tabular-nums text-emerald-900">
                  {fmtCcy(series.sale_exit?.net_proceeds, ccy)}
                </div>
                <p className="mt-0.5 text-[11px] text-emerald-800/80">
                  After CGT, selling costs, mortgage payoff.
                </p>
              </div>
            </div>

            {/* Year-by-year table */}
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      Year
                    </th>
                    <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      Operating CF
                    </th>
                    <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      Sale proceeds
                    </th>
                    <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-testid="proj-cf-table">
                  {(series.annual_cash_flows || []).map((cf, i) => {
                    const isLast = i === (series.annual_cash_flows || []).length - 1;
                    const proceeds = isLast ? series.sale_exit?.net_proceeds || 0 : 0;
                    return (
                      <tr key={`year-${i + 1}`} data-testid={`proj-row-y${i + 1}`}>
                        <td className="px-3 py-2 font-medium text-slate-700">Y{i + 1}</td>
                        <td className={`px-3 py-2 text-right tabular-nums ${cf < 0 ? "text-red-600" : "text-slate-900"}`}>
                          {fmtCcy(cf, ccy)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-emerald-700">
                          {isLast ? fmtCcy(proceeds, ccy) : "—"}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-900">
                          {fmtCcy(cf + proceeds, ccy)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Sale-exit breakdown */}
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <h4 className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Year-{years} sale breakdown
              </h4>
              <dl className="mt-3 grid grid-cols-2 gap-y-1.5 text-sm">
                <dt className="text-slate-500">Projected sale value</dt>
                <dd className="text-right tabular-nums text-slate-900">{fmtCcy(series.sale_exit?.future_value, ccy)}</dd>
                <dt className="text-slate-500">Gross gain</dt>
                <dd className="text-right tabular-nums text-slate-700">{fmtCcy(series.sale_exit?.gross_gain, ccy)}</dd>
                <dt className="text-slate-500">– Selling costs</dt>
                <dd className="text-right tabular-nums text-red-600">{fmtCcy(-(series.sale_exit?.selling_costs || 0), ccy)}</dd>
                <dt className="text-slate-500">
                  – Capital gains tax ({fmtPct(data.country_tax_context?.capital_gains_rate)})
                  {series.sale_exit?.long_term_discount_applied ? (
                    <span className="ml-1 rounded-sm bg-emerald-100 px-1 py-0.5 text-[9px] font-semibold text-emerald-700">
                      LT discount
                    </span>
                  ) : null}
                </dt>
                <dd className="text-right tabular-nums text-red-600">
                  {fmtCcy(-(series.sale_exit?.capital_gains_tax || 0), ccy)}
                </dd>
                {data.country_tax_context?.municipal_capital_gains_applicable ? (
                  <>
                    <dt className="text-slate-500">– Municipal CGT (plusvalía)</dt>
                    <dd className="text-right tabular-nums text-red-600">
                      {fmtCcy(-(series.sale_exit?.municipal_gains_tax || 0), ccy)}
                    </dd>
                  </>
                ) : null}
                <dt className="text-slate-500">– Mortgage payoff</dt>
                <dd className="text-right tabular-nums text-slate-700">
                  {fmtCcy(-(series.sale_exit?.mortgage_balance || 0), ccy)}
                </dd>
                <dt className="mt-1 border-t border-slate-100 pt-2 font-semibold text-slate-900">Net to investor</dt>
                <dd className="mt-1 border-t border-slate-100 pt-2 text-right tabular-nums font-bold text-emerald-700">
                  {fmtCcy(series.sale_exit?.net_proceeds, ccy)}
                </dd>
              </dl>
              <p className="mt-3 text-[11px] text-slate-500">
                Appreciation rate used: {fmtPct(series.sale_exit?.appreciation_rate_used)} ·
                Country: {data.country_tax_context?.country_name}
              </p>
            </div>

            {data.fx_risk_warning ? (
              <p
                data-testid="proj-fx-note"
                className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
              >
                <AlertCircle className="mr-1 inline h-3 w-3" />
                FX note: projection uses spot rate. {data.fx_risk_warning}
              </p>
            ) : null}
            <p className="text-[11px] text-slate-400">{data.disclaimer}</p>
          </div>
        ) : null}

        <div className="flex justify-end pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="proj-close"
            className="border-slate-200"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
